/**
 * Notification Types - Type definitions for notifications system
 */

// Notification type - extensible for future types
export type NotificationType = 'DEFAULT' | string;

// Date range filter options
export type DateRangeFilter = '24h' | '3d' | '1w' | '1m' | '6m' | '1y' | 'all';

// Individual notification
export interface Notification {
  id: number;
  type: NotificationType;
  payload: {
    content: string;
    [key: string]: unknown;
  };
  is_read: boolean;
  created_at: string; // ISO 8601
}

// API query parameters for fetching notifications
export interface NotificationQueryParams {
  type?: string;
  is_read?: boolean;
  created_after?: string; // ISO 8601
  created_before?: string; // ISO 8601
  skip?: number;
  limit?: number;
}

// API response for notifications list
export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
}

// Mark as read request
export interface MarkAsReadRequest {
  notification_ids: number[];
}

// Mark as read response
export interface MarkAsReadResponse {
  updated_count: number;
}

// Date range options for UI
export const DATE_RANGE_OPTIONS: { value: DateRangeFilter; label: string }[] = [
  { value: 'all', label: 'Depuis toujours' },
  { value: '24h', label: "Aujourd'hui" },
  { value: '1w', label: 'Cette semaine' },
  { value: '1m', label: 'Ce mois' },
  { value: '1y', label: 'Cette année' },
];

/**
 * Calculate the date boundary for a given date range filter
 */
export function getDateBoundary(range: DateRangeFilter): string | undefined {
  if (range === 'all') return undefined;

  const now = new Date();
  let boundary: Date;

  switch (range) {
    case '24h':
      boundary = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      break;
    case '3d':
      boundary = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
      break;
    case '1w':
      boundary = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '1m':
      boundary = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '6m':
      boundary = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      break;
    case '1y':
      boundary = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      break;
    default:
      return undefined;
  }

  return boundary.toISOString();
}

/**
 * Format a date for display (relative time)
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();

  // Check for invalid date
  if (isNaN(date.getTime())) {
    return 'Date invalide';
  }

  const diffMs = now.getTime() - date.getTime();

  // Handle future dates
  if (diffMs < 0) {
    const absDiffMs = Math.abs(diffMs);
    const absDiffSeconds = Math.floor(absDiffMs / 1000);
    const absDiffMinutes = Math.floor(absDiffSeconds / 60);
    const absDiffHours = Math.floor(absDiffMinutes / 60);
    const absDiffDays = Math.floor(absDiffHours / 24);

    if (absDiffSeconds < 60) {
      return "À l'instant";
    } else if (absDiffMinutes < 60) {
      return `Dans ${absDiffMinutes} min`;
    } else if (absDiffHours < 24) {
      return `Dans ${absDiffHours}h`;
    } else if (absDiffDays < 7) {
      return `Dans ${absDiffDays}j`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  }

  // Handle past dates
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return "À l'instant";
  } else if (diffMinutes < 60) {
    return `Il y a ${diffMinutes} min`;
  } else if (diffHours < 24) {
    return `Il y a ${diffHours}h`;
  } else if (diffDays < 7) {
    return `Il y a ${diffDays}j`;
  } else {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
}
